// Read-only vertical-rail timeline of a deliverable's lifecycle events.
// Each row renders a coloured node (filled dot / amber half-fill /
// lucide ✗ / lucide ⏳ / hollow ○) keyed by event type, with the
// effective date on the left and label + comment on the right.
// awaiting-resubmission rows carry a date-range pill consuming the
// next submitted event; failed-acceptance and its follow-up
// awaiting-resubmission both carry their attemptIndex chip.

import { clsx } from "clsx";
import { X, Hourglass } from "lucide-react";
import type {
  DeliverableLifecycleEvent,
  LifecycleEventType,
} from "../../../../types";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import AmberHalfFillDot from "../shared/AmberHalfFillDot";

interface DeliverableLifecycleTimelineProps {
  lifecycleEvents?: DeliverableLifecycleEvent[];
}

const EVENT_LABELS: Record<LifecycleEventType, string> = {
  submitted: "Submitted",
  "under-review": "Under review",
  "revision-requested": "Revision requested",
  "conditionally-accepted": "Conditionally accepted",
  "awaiting-resubmission": "Awaiting resubmission",
  "failed-acceptance": "Failed acceptance",
  accepted: "Accepted",
  rejected: "Rejected",
  closed: "Closed",
  reverted: "Reverted",
};

// Dot-style nodes (filled circle). Each entry is the full token set so
// the lint rule that bans broad regex matching on dark: classes can hold
// — tests assert on the specific tokens.
const DOT_CLASS: Partial<Record<LifecycleEventType, string>> = {
  submitted: "bg-sky-500 border-sky-500 dark:bg-sky-400 dark:border-sky-400",
  "under-review":
    "bg-sky-700 border-sky-700 dark:bg-sky-300 dark:border-sky-300",
  "revision-requested":
    "bg-amber-500 border-amber-500 dark:bg-amber-400 dark:border-amber-400",
  accepted:
    "bg-emerald-500 border-emerald-500 dark:bg-emerald-400 dark:border-emerald-400",
  rejected: "bg-red-500 border-red-500 dark:bg-red-400 dark:border-red-400",
  closed:
    "bg-slate-500 border-slate-500 dark:bg-slate-400 dark:border-slate-400",
  reverted:
    "bg-slate-500 border-slate-500 dark:bg-slate-400 dark:border-slate-400",
};

const DOT_BASE = "block h-3 w-3 rounded-full border";
const PENDING_DOT =
  "block h-3 w-3 rounded-full border border-slate-300 bg-transparent dark:border-slate-600";

// Canonical happy-path. Used to render hollow not-yet-reached rows
// after the last present event when no terminal state has been recorded.
const CANONICAL_PATH: readonly LifecycleEventType[] = [
  "submitted",
  "under-review",
  "accepted",
];

const TERMINAL: ReadonlySet<LifecycleEventType> = new Set([
  "accepted",
  "rejected",
  "closed",
]);

function nextSubmittedAfter(
  events: readonly DeliverableLifecycleEvent[],
  i: number,
): string | null {
  for (let j = i + 1; j < events.length; j++) {
    if (events[j].type === "submitted") return events[j].date;
  }
  return null;
}

function AttemptChip({ index, n }: { index: number; n: number }) {
  return (
    <span
      data-testid={`lifecycle-attempt-chip-${index}`}
      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-caption font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
    >
      Attempt {n}
    </span>
  );
}

// Amber half-fill node — uses the shared AmberHalfFillDot primitive
// so this surface and the milestone progress-dot partial state stay
// visually identical. Wire the outer testid + lifecycle-half-fill
// inner testid through so existing assertions still resolve.
function ConditionallyAcceptedNode({ index }: { index: number }) {
  return (
    <AmberHalfFillDot
      size="md"
      ariaLabel="Conditionally accepted"
      outerTestId={`lifecycle-node-conditionally-accepted-${index}`}
      testId={`lifecycle-half-fill-${index}`}
    />
  );
}

function FailedAcceptanceNode({ index }: { index: number }) {
  return (
    <span
      data-testid={`lifecycle-node-failed-acceptance-${index}`}
      role="img"
      aria-label="Failed acceptance"
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"
    >
      <X
        data-testid="lifecycle-x-icon"
        className="h-3.5 w-3.5"
        aria-hidden="true"
      />
    </span>
  );
}

function AwaitingResubmissionNode({ index }: { index: number }) {
  return (
    <span
      data-testid={`lifecycle-node-awaiting-resubmission-${index}`}
      role="img"
      aria-label="Awaiting resubmission"
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
    >
      <Hourglass
        data-testid="lifecycle-hourglass-icon"
        className="h-3.5 w-3.5"
        aria-hidden="true"
      />
    </span>
  );
}

function ResubmissionRangePill({
  index,
  start,
  end,
}: {
  index: number;
  start: string;
  end: string | null;
}) {
  const text = `${formatDayMonthYear(start)} – ${end ? formatDayMonthYear(end) : "ongoing"}`;
  return (
    <span
      data-testid={`lifecycle-resubmission-range-${index}`}
      className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-caption font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
    >
      {text}
    </span>
  );
}

function EventNode({
  event,
  index,
}: {
  event: DeliverableLifecycleEvent;
  index: number;
}) {
  if (event.type === "conditionally-accepted") {
    return <ConditionallyAcceptedNode index={index} />;
  }
  if (event.type === "failed-acceptance") {
    return <FailedAcceptanceNode index={index} />;
  }
  if (event.type === "awaiting-resubmission") {
    return <AwaitingResubmissionNode index={index} />;
  }
  const cls = DOT_CLASS[event.type];
  return (
    <span
      data-testid={`lifecycle-node-${event.type}-${index}`}
      role="img"
      aria-label={EVENT_LABELS[event.type]}
      className={clsx(DOT_BASE, cls)}
    />
  );
}

interface EventRowProps {
  event: DeliverableLifecycleEvent;
  index: number;
  resubmissionEnd: string | null;
}

function EventRow({ event, index, resubmissionEnd }: EventRowProps) {
  const showAttempt =
    typeof event.attemptIndex === "number" &&
    event.attemptIndex > 0 &&
    (event.type === "failed-acceptance" ||
      event.type === "awaiting-resubmission");
  const showRange = event.type === "awaiting-resubmission";
  return (
    <li className="grid grid-cols-[6.5rem_auto_1fr] items-start gap-3">
      <div className="pt-0.5 text-caption text-slate-500 dark:text-slate-400">
        {formatDayMonthYear(event.date)}
      </div>
      <div className="flex items-center justify-center pt-0.5">
        <EventNode event={event} index={index} />
      </div>
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body font-medium text-slate-900 dark:text-slate-100">
            {EVENT_LABELS[event.type]}
          </span>
          {showAttempt && (
            <AttemptChip index={index} n={event.attemptIndex as number} />
          )}
          {showRange && (
            <ResubmissionRangePill
              index={index}
              start={event.date}
              end={resubmissionEnd}
            />
          )}
        </div>
        {event.comment && (
          <p className="text-caption italic text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
            {event.comment}
          </p>
        )}
      </div>
    </li>
  );
}

function PendingRow({ type }: { type: LifecycleEventType }) {
  return (
    <li
      data-testid={`lifecycle-pending-${type}`}
      className="grid grid-cols-[6.5rem_auto_1fr] items-start gap-3"
    >
      <div className="pt-0.5 text-caption text-slate-400 dark:text-slate-500">
        —
      </div>
      <div className="flex items-center justify-center pt-0.5">
        <span aria-hidden="true" className={PENDING_DOT} />
      </div>
      <div className="text-body text-slate-500 dark:text-slate-400">
        {EVENT_LABELS[type]}
      </div>
    </li>
  );
}

function EmptyRow() {
  return (
    <li
      data-testid="lifecycle-empty-row"
      className="grid grid-cols-[6.5rem_auto_1fr] items-start gap-3"
    >
      <div className="pt-0.5 text-caption text-slate-400 dark:text-slate-500">
        —
      </div>
      <div className="flex items-center justify-center pt-0.5">
        <span aria-hidden="true" className={PENDING_DOT} />
      </div>
      <div className="text-body text-slate-500 dark:text-slate-400">
        Not yet started
      </div>
    </li>
  );
}

export default function DeliverableLifecycleTimeline({
  lifecycleEvents,
}: DeliverableLifecycleTimelineProps) {
  const events = lifecycleEvents ?? [];

  if (events.length === 0) {
    return (
      <ol
        data-testid="deliverable-lifecycle-timeline"
        className="space-y-3"
      >
        <EmptyRow />
      </ol>
    );
  }

  const presentTypes = new Set(events.map((e) => e.type));
  const hasTerminal = events.some((e) => TERMINAL.has(e.type));
  const pendingTypes: LifecycleEventType[] = hasTerminal
    ? []
    : CANONICAL_PATH.filter((t) => !presentTypes.has(t));

  return (
    <ol
      data-testid="deliverable-lifecycle-timeline"
      className="space-y-3"
    >
      {events.map((event, i) => (
        <EventRow
          key={`${event.type}-${i}`}
          event={event}
          index={i}
          resubmissionEnd={
            event.type === "awaiting-resubmission"
              ? nextSubmittedAfter(events, i)
              : null
          }
        />
      ))}
      {pendingTypes.map((t) => (
        <PendingRow key={`pending-${t}`} type={t} />
      ))}
    </ol>
  );
}
